/* src/pages/admin/AdminDashboard.js */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const StatCard = ({ title, value, icon }) => (
    <div className="admin-stat-card">
        <div className="stat-icon-wrapper">
            <span className="material-icons">{icon}</span>
        </div>
        <div className="stat-info">
            <span className="stat-value">{value}</span>
            <span className="stat-title">{title}</span>
        </div>
    </div>
);

// Test Email Modal Component
const TestEmailModal = ({ isOpen, onClose, onSend }) => {
    const [email, setEmail] = useState('');
    const [contentType, setContentType] = useState('article');
    const [isSending, setIsSending] = useState(false);
    const [message, setMessage] = useState('');
    const [showPreview, setShowPreview] = useState(false);

    const handleSend = async () => {
        if (!email) {
            setMessage('Please enter an email address');
            return;
        }

        setIsSending(true);
        setMessage('');

        try {
            const token = localStorage.getItem('app_token');
            const response = await axios.post('/api/notifications/test', {
                email,
                contentType
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setMessage('Test email sent successfully!');
            setTimeout(() => {
                onClose();
                setEmail('');
                setContentType('article');
                setMessage('');
                setShowPreview(false);
            }, 2000);
        } catch (error) {
            console.error('Test email error:', error);
            let errorMessage = 'Failed to send test email';
            
            if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.response?.status === 403) {
                errorMessage = 'Access denied. Admin privileges required.';
            } else if (error.response?.status === 400) {
                errorMessage = 'Invalid request. Please check your input.';
            } else if (error.response?.status === 500) {
                errorMessage = 'Server error. Please check the backend logs.';
            } else if (error.code === 'NETWORK_ERROR') {
                errorMessage = 'Network error. Please check your connection.';
            }
            
            setMessage(errorMessage);
        } finally {
            setIsSending(false);
        }
    };

    const getPreviewContent = () => {
        const contentTypeLabels = {
            article: 'Article',
            video: 'Video',
            reel: 'Reel',
            podcast: 'Podcast'
        };

        return {
            title: 'Test Notification',
            contentType: contentTypeLabels[contentType],
            content: 'This is a test notification to verify email functionality.',
            contentUrl: 'https://hemant-jha.com',
            authorName: 'Hemant Jha',
            category: 'Test'
        };
    };

    if (!isOpen) return null;

    const previewData = getPreviewContent();

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header">
                    <h3>Send Test Notification Email</h3>
                    <button onClick={onClose} className="modal-close">
                        <span className="material-icons">close</span>
                    </button>
                </div>
                <div className="modal-body">
                    <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter user's email address"
                            className="form-input"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="contentType">Content Type</label>
                        <select
                            id="contentType"
                            value={contentType}
                            onChange={(e) => setContentType(e.target.value)}
                            className="form-select"
                        >
                            <option value="article">Article</option>
                            <option value="video">Video</option>
                            <option value="reel">Reel</option>
                            <option value="podcast">Podcast</option>
                        </select>
                    </div>
                    
                    <div className="form-group">
                        <button
                            type="button"
                            onClick={() => setShowPreview(!showPreview)}
                            className="btn-secondary"
                            style={{ marginBottom: '1rem' }}
                        >
                            <span className="material-icons" style={{ marginRight: '8px', fontSize: '16px' }}>
                                {showPreview ? 'visibility_off' : 'visibility'}
                            </span>
                            {showPreview ? 'Hide Preview' : 'Show Preview'}
                        </button>
                        
                        {showPreview && (
                            <div className="email-preview">
                                <h4>Email Preview</h4>
                                <div className="preview-content">
                                    <p><strong>Subject:</strong> New {previewData.contentType} Available: {previewData.title}</p>
                                    <p><strong>From:</strong> Hemant Jha &lt;support@hemant-jha.com&gt;</p>
                                    <p><strong>To:</strong> {email}</p>
                                    <hr />
                                    <p><strong>Content:</strong></p>
                                    <p>Hi there!</p>
                                    <p>I've just published a new {contentType}: <strong>{previewData.title}</strong></p>
                                    <p>{previewData.content}</p>
                                    <p><strong>Category:</strong> {previewData.category}</p>
                                    <p><strong>Author:</strong> {previewData.authorName}</p>
                                    <p><a href={previewData.contentUrl}>Read the full {contentType} →</a></p>
                                    <hr />
                                    <p style={{ fontSize: '12px', color: '#666' }}>
                                        You're receiving this because you're subscribed to {contentType} notifications. 
                                        <br />
                                        <a href={`/unsubscribe?email=${email}&type=${contentType}`}>Unsubscribe from {contentType} notifications</a>
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {message && (
                        <div className={`message ${message.includes('successfully') ? 'success' : 'error'}`}>
                            {message}
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <button onClick={onClose} className="btn-secondary">
                        Cancel
                    </button>
                    <button 
                        onClick={handleSend} 
                        className="btn-primary"
                        disabled={isSending}
                    >
                        {isSending ? 'Sending...' : 'Send Test Email'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Helper function to process data for the content chart
const processContentData = (contentByMonth) => {
    const dataMap = new Map();
    contentByMonth.forEach(({ type, month, count }) => {
        if (!dataMap.has(month)) {
            dataMap.set(month, { month });
        }
        dataMap.get(month)[type] = count;
    });
    return Array.from(dataMap.values());
};

const AdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showTestEmailModal, setShowTestEmailModal] = useState(false);

    useEffect(() => {
        const fetchDashboardData = async () => {
            setIsLoading(true);
            try {
                const token = localStorage.getItem('app_token');
                const config = { headers: { Authorization: `Bearer ${token}` } };
                
                const [statsRes, analyticsRes] = await Promise.all([
                    axios.get('/api/admin/stats', config),
                    axios.get('/api/admin/analytics', config)
                ]);

                setStats(statsRes.data);
                setAnalyticsData(analyticsRes.data);
                
            } catch (err) {
                setError("Failed to load dashboard data.");
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchDashboardData();
    }, []);

    if (isLoading) return <p>Loading dashboard...</p>;
    if (error) return <p className="error-message">{error}</p>;

    const processedContentData = analyticsData ? processContentData(analyticsData.contentByMonth) : [];
    
    // Read the API base URL from the environment variable.
    // Fallback to localhost for local development if the variable is not set.
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

    return (
        <div>
            <div className="admin-content-header" style={{ paddingBottom: 0 }}>
                <h3 className="admin-section-title">Dashboard</h3>
            </div>

            <div className="admin-stats-grid">
                <StatCard title="Total Users" value={stats?.users || 0} icon="people" />
                <StatCard title="Total Articles" value={stats?.articles || 0} icon="article" />
                <StatCard title="Total Videos" value={stats?.videos || 0} icon="videocam" />
                <StatCard title="Total Podcasts" value={stats?.podcasts || 0} icon="headphones" />
                <StatCard title="Total Reels" value={stats?.reels || 0} icon="video_library" />
                <StatCard title="Custom Books Generated" value={stats?.customizedBooks || 0} icon="auto_stories" />
            </div>

            <div className="admin-charts-grid">
                <div className="chart-container">
                    <h4 className="chart-title">New Users (Last 12 Months)</h4>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={analyticsData?.usersByMonth} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="count" name="New Users" stroke="#4f46e5" strokeWidth={2} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
                <div className="chart-container">
                    <h4 className="chart-title">Content Published (Last 12 Months)</h4>
                     <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={processedContentData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="videos" stackId="a" fill="#82ca9d" name="Videos" />
                            <Bar dataKey="reels" stackId="a" fill="#ffc658" name="Reels" />
                            <Bar dataKey="podcasts" stackId="a" fill="#ff8042" name="Podcasts" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="admin-actions-footer">
                <h4 className="admin-section-subtitle">App Integrations</h4>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <a
                        href={`${API_BASE_URL}/api/admin/linkedin/auth`}
                        className="btn-primary"
                        style={{ backgroundColor: '#0077b5', textDecoration: 'none' }}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Authorize LinkedIn
                    </a>
                    <a
                        href={`${API_BASE_URL}/api/youtube/auth`}
                        className="btn-primary"
                        style={{ backgroundColor: '#FF0000', textDecoration: 'none' }}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Authorize YouTube Channel
                    </a>
                    <button
                        onClick={() => setShowTestEmailModal(true)}
                        className="btn-primary"
                        style={{ backgroundColor: '#16a085', textDecoration: 'none' }}
                    >
                        <span className="material-icons" style={{ marginRight: '8px', fontSize: '16px' }}>email</span>
                        Send Test Email
                    </button>
                </div>
            </div>

            <TestEmailModal
                isOpen={showTestEmailModal}
                onClose={() => setShowTestEmailModal(false)}
                onSend={() => setShowTestEmailModal(false)}
            />
        </div>
    );
};

export default AdminDashboard;
